import React from "react";
import {
  Alert,
  Box,
  Typography,
  TextField,
  Paper,
  Chip,
  IconButton,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import CloseIcon from "@mui/icons-material/Close";
import { FlowEmailAction } from "../../../../../stores/flowsStore";
import { useEmailConfiguration } from "./useEmailConfiguration";

interface EmailConfigurationProps {
  emailAction: FlowEmailAction | null;
  onChange: (emailAction: FlowEmailAction | null) => void;
  onRemove?: () => void;
}

export const EmailConfiguration: React.FC<EmailConfigurationProps> = ({
  emailAction,
  onChange,
  onRemove,
}) => {
  const {
    bodyRef,
    variables,
    gmailConnected,
    handleChange,
    insertVariableIntoBody,
  } = useEmailConfiguration({ emailAction, onChange });

  return (
    <Paper variant="outlined" sx={{ p: 2, position: "relative" }}>
      {onRemove && (
        <IconButton
          size="small"
          onClick={onRemove}
          sx={{ position: "absolute", top: 8, right: 8 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )}

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <EmailIcon color="action" />
        <Typography variant="subtitle1">Send Email</Typography>
      </Box>

      {gmailConnected === false && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Gmail is not connected. Go to Dashboard to connect your Gmail account
          before using email actions.
        </Alert>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {/* To Field */}
        <TextField
          label="To"
          placeholder="recipient@example.com"
          value={emailAction?.to || ""}
          onChange={(e) => handleChange("to", e.target.value)}
          fullWidth
          required
          size="small"
        />

        {/* CC Field */}
        <TextField
          label="CC"
          placeholder="cc@example.com (optional)"
          value={emailAction?.cc || ""}
          onChange={(e) => handleChange("cc", e.target.value)}
          fullWidth
          size="small"
        />

        {/* Subject Field */}
        <TextField
          label="Subject Line"
          placeholder="Enter email subject"
          value={emailAction?.subject || ""}
          onChange={(e) => handleChange("subject", e.target.value)}
          fullWidth
          required
          size="small"
        />

        {/* Body Field */}
        <TextField
          label="Message Body"
          placeholder="Enter email body content..."
          value={emailAction?.body || ""}
          onChange={(e) => handleChange("body", e.target.value)}
          fullWidth
          required
          multiline
          rows={6}
          inputRef={bodyRef}
        />

        {/* Available Variables Section */}
        <Paper variant="outlined" sx={{ p: 2, bgcolor: "grey.50" }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Available Variables
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
            {variables.map((variable) => (
              <Chip
                key={variable.key}
                label={`{{${variable.key}}}`}
                size="small"
                variant="outlined"
                onClick={() => insertVariableIntoBody(variable.key)}
                sx={{
                  cursor: "pointer",
                  "&:hover": {
                    bgcolor: "primary.50",
                    borderColor: "primary.main",
                  },
                }}
              />
            ))}
          </Box>
          <Typography variant="caption" color="text.secondary">
            Click a variable to insert it into the message body
          </Typography>
        </Paper>
      </Box>
    </Paper>
  );
};
